<template>
  <div>
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" label="Loading..." />
      <p class="mt-2 text-muted">جاري تحميل النشاط...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-2" />
      <h4>حدث خطأ</h4>
      <p class="text-muted">{{ error }}</p>
      <b-button variant="primary" @click="$router.go(-1)">
        <feather-icon icon="ArrowLeftIcon" class="mr-50" />
        العودة
      </b-button>
    </div>

    <!-- Activity Content -->
    <div v-else-if="activity">
      <!-- Activity Header -->
      <b-card class="mb-2">
        <b-row align-v="center">
          <b-col cols="12" md="8">
            <div class="d-flex align-items-center mb-1">
              <b-badge :variant="getActivityVariant(activity.activity_type)" pill class="mr-1">
                {{ activity.activity_type }}
              </b-badge>
              <b-badge variant="light-secondary" pill>
                {{ activity.code }}
              </b-badge>
            </div>
            <h3 class="mb-0">{{ activity.name }}</h3>
          </b-col>
          <b-col cols="12" md="4" class="text-md-right mt-1 mt-md-0">
            <b-button variant="outline-secondary" @click="$router.go(-1)">
              <feather-icon icon="ArrowLeftIcon" class="mr-50" />
              العودة
            </b-button>
          </b-col>
        </b-row>
      </b-card>

      <!-- Homework/Project Component -->
      <homework-attend v-if="activity.assignment_and_projects_data" :activity="activity" />

      <!-- Forum Component -->
      <forum-attend v-else-if="activity.forums_data" :activity="activity" />

      <!-- Exam Component -->
      <exam-attend v-else-if="activity.short_exams_data" :activity="activity" />

      <!-- Unknown Type -->
      <b-card v-else class="text-center py-5">
        <feather-icon icon="AlertOctagonIcon" size="48" class="text-warning mb-2" />
        <h4>نوع نشاط غير معروف</h4>
        <b-button variant="primary" @click="$router.go(-1)">
          <feather-icon icon="ArrowLeftIcon" class="mr-50" />
          العودة
        </b-button>
      </b-card>
    </div>
  </div>
</template>

<script>
import ActivitiesController from "@/Controllers/ActivitiesController";
import HomeworkAttend from "./components/HomeworkAttend.vue";
import ForumAttend from "./components/ForumAttend.vue";
import ExamAttend from "./components/ExamAttend.vue";
import Auth from "@/Apis/Auth";
import Swal from "sweetalert2";

export default {
  components: {
    HomeworkAttend,
    ForumAttend,
    ExamAttend,
  },
  data() {
    return {
      activity: null,
      loading: true,
      error: null,
      totalAttempts: 0,
    };
  },
  computed: {
    maxAttempts() {
      // Only exams have attempt limits
      if (this.activity && this.activity.short_exams_data) {
        return this.activity.short_exams_data.attemps || 3;
      }
      return null; // No limit for homework/forum
    },
    
    isWithinDateRange() {
      // Check if current date is within activity date range
      if (!this.activity) return false;
      
      const now = new Date();
      const startDate = new Date(this.activity.from_date);
      const endDate = new Date(this.activity.to_date);
      
      // Check if started
      if (now < startDate) {
        return false;
      }
      
      // For homework/project, check disallow_delayed_homeworks setting
      if (this.activity.assignment_and_projects_data) {
        const disallowLateSubmission = this.activity.assignment_and_projects_data.disallow_delayed_homeworks;
        // If disallow_delayed_homeworks is 1, don't allow submissions after end date
        if (disallowLateSubmission === 1 && now > endDate) {
          return false;
        }
        // If disallow_delayed_homeworks is 0, allow late submissions
        return true;
      }
      
      // For other types (forum, exam), check end date normally
      return now <= endDate;
    },
    
    canAttend() {
      // Check date range first
      if (!this.isWithinDateRange) {
        return false;
      }
      
      // For exams, check attempt limit
      if (this.activity && this.activity.short_exams_data) {
        return this.totalAttempts < this.maxAttempts;
      }
      
      // For homework/project, check allow_resubmit setting
      if (this.activity && this.activity.assignment_and_projects_data) {
        const allowResubmit = this.activity.assignment_and_projects_data.allow_resubmit;
        // If allow_resubmit is 0 and student already submitted, don't allow resubmission
        if (allowResubmit === 0 && this.totalAttempts > 0) {
          return false;
        }
      }
      
      // For forum, only check date range
      return true;
    },
  },
  async created() {
    await this.fetchActivity();
    await this.fetchAttempts();
    await this.validateAccess();
  },
  methods: {
    async fetchActivity() {
      try {
        this.loading = true;
        this.error = null;

        const activityId = this.$route.params.id;
        if (!activityId) {
          this.error = "معرّف النشاط غير موجود";
          return;
        }

        const activitiesController = new ActivitiesController();
        
        // First, get basic activity info to determine type
        const basicActivity = await activitiesController.getActivityForStudent(activityId);
        
        // If it's an exam, use the exam-specific endpoint
        if (basicActivity && basicActivity.short_exams_data) {
          const examActivity = await activitiesController.getActivityForStudentExam(activityId);
          this.activity = examActivity;
        } else {
          this.activity = basicActivity;
        }

        // Set page title
        if (this.activity && this.activity.name) {
          this.$mobileTitle.label = `تقديم: ${this.activity.name}`;
        }
      } catch (error) {
        this.error = "حدث خطأ في تحميل بيانات النشاط";
      } finally {
        this.loading = false;
      }
    },

    async fetchAttempts() {
      try {
        const activityId = this.$route.params.id;
        const user = Auth.USER;
        
        if (!activityId || !user || !user.id) {
          return;
        }

        const activitiesController = new ActivitiesController();
        const result = await activitiesController.getActivityAttempt(activityId, user.id);
        
        if (result && result.success) {
          this.totalAttempts = result.total_attempts || 0;
        }
      } catch (error) {
        // Handle silently
      }
    },

    async validateAccess() {
      // After loading activity and attempts, check if student can attend
      if (!this.activity) return;
      
      if (!this.canAttend) {
        const now = new Date();
        const startDate = new Date(this.activity.from_date);
        const endDate = new Date(this.activity.to_date);
        
        let message = '';
        if (now < startDate) {
          message = 'لم يبدأ موعد النشاط بعد';
        } else if (now > endDate) {
          // For homework, check if late submission is disallowed
          if (this.activity.assignment_and_projects_data) {
            const disallowLateSubmission = this.activity.assignment_and_projects_data.disallow_delayed_homeworks;
            if (disallowLateSubmission === 1) {
              message = 'انتهى موعد النشاط ولا يسمح بالتسليم المتأخر';
            } else {
              message = 'انتهى موعد النشاط';
            }
          } else {
            message = 'انتهى موعد النشاط';
          }
        } else if (this.activity.short_exams_data && this.totalAttempts >= this.maxAttempts) {
          message = `لقد استنفدت جميع المحاولات المسموحة (${this.maxAttempts})`;
        } else if (this.activity.assignment_and_projects_data) {
          // Check homework resubmission
          const allowResubmit = this.activity.assignment_and_projects_data.allow_resubmit;
          if (allowResubmit === 0 && this.totalAttempts > 0) {
            message = 'لقد قمت بالتسليم ولا يسمح بإعادة التسليم';
          } else {
            message = 'لا يمكنك حضور هذا النشاط حالياً';
          }
        } else {
          message = 'لا يمكنك حضور هذا النشاط حالياً';
        }

        // Show warning and redirect immediately (don't wait for OK)
        Swal.fire({
          icon: "warning",
          title: "تنبيه",
          text: message,
          timer: 2000,
          showConfirmButton: false,
        });
        
        // Redirect immediately
        this.$router.replace({
          name: "activities-v2-detail",
          params: { id: this.activity.id },
        });
      }
    },

    getActivityVariant(activityType) {
      const variants = {
        واجب: "success",
        منتدي: "info",
        "امتحان قصير": "danger",
        "بحث قصير": "warning",
        مشروع: "primary",
      };
      return variants[activityType] || "secondary";
    },
  },
};
</script>
